import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import {
  LayoutDashboard, Users, BookOpen, Layers, MessageSquare, UserCog,
  CreditCard, Mic, Video, Brain, Target, HelpCircle, ClipboardList,
  Shield, Settings, LogOut, Calendar, Code, FileText, Moon, Sun,
  Building2, ScrollText, Clock, Zap, PlayCircle, Award, LifeBuoy,
  MessageCircle, Monitor, FolderTree, ClipboardCheck, BarChart3, Database,
  Lock, Wallet, GraduationCap, Trash2,
} from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import vcSimpleLogo from '../Assets/Images/Logos/simple_logo.png';
import lmsSimpleLogo from '../Assets/Images/Logos/favicon.png';
import { getCurrentUser, logout as authLogout } from '../utils/auth';
import { hasPermission, hasAnyPermission } from '../utils/permissions';
import apiClient from '../utils/apiClient';
import { useBranding } from '../contexts/BrandingContext';
import { usePlan } from '../contexts/PlanContext';
import { useTheme } from '../contexts/ThemeContext';

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith('lmscampus.com') || h === 'localhost';
};
const simpleLogo = isLmsCampus() ? lmsSimpleLogo : vcSimpleLogo;

/**
 * Menu group definitions per user role.
 * Each group: { label?: string, items: [...] }
 * Items: { label, icon, route, badge?, badgeVariant?, beta?, permission? }
 *
 * For admin users, the `permission` key controls visibility.
 * superadmin users see everything (hasPermission always returns true).
 */
const getMenuGroups = (userLoggedIn, studentSelfScheduling, hasPlanFeature, isFeatureHidden) => {
  if (!userLoggedIn) return [];

  if (userLoggedIn.usertype === "tenantadmin") {
    return [
      {
        items: [
          { label: 'Dashboard', icon: LayoutDashboard, route: '/sa' },
          { label: 'Institutes', icon: Building2, route: '/sa/institutes' },
          { label: 'Plans', icon: CreditCard, route: '/sa/plans' },
          { label: 'Zoom Hosts', icon: Monitor, route: '/sa/zoom-hosts' },
          { label: 'Zoho Meeting', icon: Video, route: '/sa/zoho-meeting' },
          { label: 'Users', icon: Users, route: '/sa/users' },
          { label: 'Audit Logs', icon: ScrollText, route: '/sa/audit-logs' },
          { label: 'Payouts', icon: Wallet, route: '/sa/payouts' },
          { label: 'Analytics', icon: BarChart3, route: '/sa/analytics' },
          { label: 'Form Templates', icon: FileText, route: '/sa/form-templates' },
          { label: 'Coding Questions', icon: Code, route: '/sa/coding-questions' },
          { label: 'Sales Leads', icon: MessageSquare, route: '/sa/sales-leads' },
          { label: 'Service Invoices', icon: FileText, route: '/sa/service-invoices' },
          { label: 'Support', icon: LifeBuoy, route: '/sa/support' },
          { label: 'Storage', icon: Database, route: '/sa/storage' },
        ],
      },
      {
        label: 'Administration',
        items: [
          { label: 'Settings', icon: Settings, route: '/sa/settings' },
          { label: 'WhatsApp Config', icon: MessageCircle, route: '/sa/whatsapp' },
          { label: 'WhatsApp Inbox', icon: MessageCircle, route: '/sa/whatsapp-inbox' },
          { label: 'Zoho CRM Config', icon: Zap, route: '/sa/zoho' },
          { label: 'Account', icon: UserCog, route: '/sa/account-settings' },
        ],
      },
    ];
  }

  if (userLoggedIn.usertype === "superadmin" || userLoggedIn.usertype === "admin") {
    // Build full menu, then filter by permission
    const adminMenu = [
      {
        items: [
          { label: 'Dashboard', icon: LayoutDashboard, route: '/dashboard', permission: 'view_dashboards' },
          { label: 'Students', icon: Users, route: '/students', permission: 'view_students' },
          { label: 'Courses', icon: BookOpen, route: '/courses', permission: 'view_courses' },
          { label: 'Batches', icon: Layers, route: '/batches', permission: 'manage_batches' },
          { label: 'Enquiries', icon: MessageSquare, route: '/enquiries', permission: 'view_enquiries', planFeature: 'enquiryManagement' },
          { label: 'Instructors', icon: UserCog, route: '/instructors', permission: 'view_instructors' },
          { label: 'Fees', icon: CreditCard, route: '/fees', permission: 'view_fees', planFeature: 'feeManagement' },
          { label: 'Earnings', icon: Wallet, route: '/earnings', permission: 'view_fees', planFeature: 'feeManagement' },
          { label: 'Self-Paced', icon: PlayCircle, route: '/self-paced', permission: 'view_courses', planFeature: 'selfPacedCourses' },
          { label: 'Batch Chat', icon: MessageCircle, route: '/chat' },
          { label: 'Feedback', icon: MessageSquare, route: '/feedback' },
          { label: 'Enrollment Requests', icon: GraduationCap, route: '/enrollment-requests', permission: 'view_students' },
          { label: 'Attendance', icon: ClipboardCheck, route: '/attendance', permission: 'manage_batches' },
        ],
      },
      {
        label: 'Toolkit',
        items: [
          { label: 'Enquiry Forms', icon: FileText, route: '/enquiry-forms', permission: 'view_enquiries', planFeature: 'enquiryFormBuilder' },
          { label: 'Certificate Design', icon: Award, route: '/self-paced/certificate-settings', permission: 'view_courses', planFeature: 'selfPacedCourses' },
        ],
      },
      {
        label: 'Assessments',
        items: [
          { label: 'Mock Interview History', icon: Mic, route: '/mock-interview/history', permission: 'view_ai_attempts', planFeature: 'aiMock' },
          { label: 'Interviews', icon: Video, route: '/interviews', permission: 'manage_interviews', planFeature: 'interviews' },
          { label: 'AI Questions', icon: Brain, route: '/ai-questions', permission: 'view_ai_attempts', planFeature: 'aiQuestionBank' },
          { label: 'AI Attempts', icon: Target, route: '/ai-attempts', permission: 'view_ai_attempts', planFeature: 'aiLearning' },
          { label: 'Coding Questions', icon: HelpCircle, route: '/questions-management', permission: 'manage_assignments', planFeature: 'aiQuestionBank' },
          { label: 'Coding Submissions', icon: Code, route: '/coding-submissions', permission: 'manage_assignments', planFeature: 'codingPractice' },
          { label: 'Assignments', icon: ClipboardList, route: '/assignments', permission: 'manage_assignments', planFeature: 'assignments' },
          { label: 'Batch Assignments', icon: FileText, route: '/batch-assignments', permission: 'manage_assignments' },
          { label: 'Quiz Categories', icon: FolderTree, route: '/quiz-bank/categories', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Question Bank', icon: HelpCircle, route: '/quiz-bank', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Assessments', icon: ClipboardCheck, route: '/assessments', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Assessment Analytics', icon: BarChart3, route: '/assessment-analytics', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Bulk Operations', icon: Database, route: '/assessment-bulk', permission: 'manage_assignments', planFeature: 'quizAssessment' },
        ],
      },
      {
        label: 'Administration',
        items: [
          ...(userLoggedIn.usertype === "superadmin"
            ? [{ label: 'Admins', icon: Shield, route: '/admins', permission: 'manage_admins' }]
            : []),
          { label: 'Branches', icon: Building2, route: '/branches', permission: 'manage_branches', planFeature: 'multiBranch' },
          { label: 'Institute Calendar', icon: Calendar, route: '/institute/calendar' },
          { label: 'Billing & Plan', icon: CreditCard, route: '/billing' },
          { label: 'Service Invoices', icon: FileText, route: '/service-invoices' },
          { label: 'Storage', icon: Database, route: '/storage' },
          { label: 'Trash', icon: Trash2, route: '/trash' },
          { label: 'WhatsApp', icon: MessageCircle, route: '/whatsapp', planFeature: 'whatsapp' },
          { label: 'Support', icon: LifeBuoy, route: '/support/tickets' },
          { label: 'Settings', icon: Settings, route: '/settings', permissionFn: () => hasAnyPermission('settings_general', 'settings_notifications', 'settings_branding', 'settings_payments', 'settings_zoom') },
          { label: 'Account', icon: UserCog, route: '/account-settings' },
        ],
      },
    ];

    // Filter items by permission and plan access — hide items not in plan
    return adminMenu
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => {
            if (item.permissionFn && !item.permissionFn()) return false;
            if (item.permission && !hasPermission(item.permission)) return false;
            if (item.planFeature && isFeatureHidden(item.planFeature)) return false;
            if (item.planFeature && !hasPlanFeature(item.planFeature)) return false;
            return true;
          }),
      }))
      .filter(group => group.items.length > 0);
  }

  if (userLoggedIn.usertype === "instructor") {
    const instructorMenu = [
      {
        items: [
          { label: 'Profile', icon: LayoutDashboard, route: '/i/' },
          { label: 'Attendance', icon: ClipboardCheck, route: '/i/attendance', planFeature: 'offlineAttendance' },
          { label: 'Batch Chat', icon: MessageCircle, route: '/i/chat' },
          { label: 'Account', icon: Settings, route: '/i/account-settings' },
        ],
      },
      {
        label: 'Assessments',
        items: [
          { label: 'Questions Management', icon: HelpCircle, route: '/i/questions-management', planFeature: 'aiQuestionBank' },
          { label: 'Coding Submissions', icon: Code, route: '/i/coding-submissions', planFeature: 'codingPractice' },
          { label: 'Assignments', icon: ClipboardList, route: '/i/assignments', planFeature: 'assignments' },
          { label: 'Batch Assignments', icon: FileText, route: '/i/batch-assignments' },
        ],
      },
      {
        label: 'Schedule',
        items: [
          { label: 'Calendar', icon: Calendar, route: '/i/calendar' },
          { label: 'Support', icon: LifeBuoy, route: '/i/support' },
        ],
      },
    ];

    return instructorMenu
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => {
            if (item.planFeature && isFeatureHidden(item.planFeature)) return false;
            if (item.planFeature && !hasPlanFeature(item.planFeature)) return false;
            return true;
          }),
      }))
      .filter(group => group.items.length > 0);
  }

  if (userLoggedIn.usertype === "student") {
    const studentMenu = [
      {
        items: [
          { label: 'Profile', icon: LayoutDashboard, route: '/s/' },
          { label: 'Batch Chat', icon: MessageCircle, route: '/s/chat' },
          { label: 'My Learning', icon: BookOpen, route: '/s/learning' },
        ],
      },
      {
        label: 'Courses',
        items: [
          { label: 'Browse Courses', icon: GraduationCap, route: '/s/courses' },
          { label: 'My Applications', icon: ClipboardList, route: '/s/courses/my-requests' },
        ],
      },
      {
        label: 'Self-Paced',
        items: [
          { label: 'Browse Courses', icon: PlayCircle, route: '/s/self-paced', planFeature: 'selfPacedCourses' },
          { label: 'My Enrollments', icon: BookOpen, route: '/s/self-paced/enrollments', planFeature: 'selfPacedCourses' },
          { label: 'Certificates', icon: Award, route: '/s/self-paced/certificates', planFeature: 'selfPacedCourses' },
        ],
      },
      {
        label: 'AI & Practice',
        items: [
          { label: 'Mock With AI', icon: Mic, route: '/s/mock-interview/setup', beta: true, planFeature: 'aiMock' },
          { label: 'Mock Interview History', icon: FileText, route: '/s/mock-interview/history', planFeature: 'aiMock' },
          { label: 'Learn with AI', icon: Brain, route: '/s/learn-with-ai', planFeature: 'aiLearning' },
          { label: 'AI History', icon: FileText, route: '/s/learn-ai/history', planFeature: 'aiLearning' },
          { label: 'Coding Practice', icon: Code, route: '/s/practice', planFeature: 'codingPractice' },
        ],
      },
      {
        label: 'Assessments',
        items: [
          { label: 'Assignments', icon: ClipboardList, route: '/s/assignments', planFeature: 'assignments' },
          { label: 'My Interviews', icon: Video, route: '/s/interviews', planFeature: 'interviews' },
          { label: 'Quizzes & Tests', icon: ClipboardCheck, route: '/s/assessments', planFeature: 'quizAssessment' },
        ],
      },
      {
        label: 'Schedule',
        items: [
          { label: 'Feedback', icon: MessageSquare, route: '/s/feedback' },
          { label: 'Calendar', icon: Calendar, route: '/s/calendar' },
          { label: 'Support', icon: LifeBuoy, route: '/s/support' },
          { label: 'Account', icon: Settings, route: '/s/account-settings' },
        ],
      },
    ];

    return studentMenu
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => {
            if (item.planFeature && isFeatureHidden(item.planFeature)) return false;
            if (item.planFeature && !hasPlanFeature(item.planFeature)) return false;
            return true;
          }),
      }))
      .filter(group => group.items.length > 0);
  }

  return [];
};

/**
 * Single menu group with optional label and separator
 */
const MenuGroup = ({ group, onNavItemClick, isLast }) => (
  <div className="sidebar-group">
    {group.label && (
      <div className="sidebar-group-label">{group.label}</div>
    )}
    <div className="sidebar-group-content">
      {group.items.map((item) => {
        if (!item) return null;
        const Icon = item.icon;
        return (
          <NavLink
            to={item.route}
            key={item.label}
            end
            onClick={onNavItemClick}
            className={({ isActive }) =>
              `sidebar-menu-item${isActive && !item.locked ? ' active' : ''}${item.locked ? ' sidebar-menu-item--locked' : ''}`
            }
          >
            <span className="sidebar-icon">
              <Icon size={18} strokeWidth={1.8} />
            </span>
            <span className="sidebar-label">{item.label}</span>
            {item.locked && (
              <span className="sidebar-lock-icon">
                <Lock size={14} />
              </span>
            )}
            {!item.locked && item.badge && (
              <span className={`sidebar-badge${item.badgeVariant === 'highlight' ? ' sidebar-badge--highlight' : ''}`}>
                {item.badge}
              </span>
            )}
            {!item.locked && item.beta && (
              <span className="sidebar-badge sidebar-badge--beta">Beta</span>
            )}
          </NavLink>
        );
      })}
    </div>
    {!isLast && <div className="sidebar-separator" />}
  </div>
);

/**
 * Compact trial banner for the sidebar — only shows for trial-status institutes.
 */
const SidebarTrialBanner = ({ trial, onClick }) => {
  if (!trial?.active) return null;
  const isUrgent = trial.daysLeft <= 3;
  return (
    <div
      className={`sidebar-trial-banner ${isUrgent ? 'sidebar-trial-banner--urgent' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className="sidebar-trial-banner__icon">
        {isUrgent ? <Clock size={16} /> : <Clock size={16} />}
      </div>
      <div className="sidebar-trial-banner__text">
        {trial.daysLeft === 0 ? (
          <span>Trial expires <strong>today</strong></span>
        ) : (
          <span><strong>{trial.daysLeft}d</strong> left in trial</span>
        )}
      </div>
      <div className="sidebar-trial-banner__cta">
        <Zap size={12} /> Upgrade
      </div>
    </div>
  );
};

// Sidebar component
const Sidebar = ({ show, onHide, isOffcanvas }) => {
  const navigate = useNavigate();
  const userLoggedIn = getCurrentUser();
  const branding = useBranding();
  const { hasPlanFeature, isFeatureHidden, trial } = usePlan();
  const { unreadChatCount, unreadWhatsAppCount } = useSocket();
  const { theme, toggleTheme } = useTheme();
  const [studentSelfScheduling, setStudentSelfScheduling] = useState(false);
  const [pendingPayoutsCount, setPendingPayoutsCount] = useState(0);

  useEffect(() => {
    if (userLoggedIn?.usertype !== 'student') return;
    apiClient.get('/institute/config')
      .then(res => setStudentSelfScheduling(!!res.data?.config?.studentSelfScheduling))
      .catch(() => {});
  }, [userLoggedIn?.usertype]);

  useEffect(() => {
    if (userLoggedIn?.usertype !== 'tenantadmin') return;
    apiClient.get('/sa/payouts/pending-count')
      .then(res => setPendingPayoutsCount(res.data?.count || 0))
      .catch(() => {});
  }, [userLoggedIn?.usertype]);

  const menuGroups = React.useMemo(
    () => {
      const groups = getMenuGroups(userLoggedIn, studentSelfScheduling, hasPlanFeature, isFeatureHidden);
      // Inject unread chat badge on "Batch Chat" items
      if (unreadChatCount > 0) {
        for (const group of groups) {
          for (const item of group.items) {
            if (item.label === 'Batch Chat') {
              item.badge = unreadChatCount > 99 ? '99+' : String(unreadChatCount);
              item.badgeVariant = 'highlight';
            }
          }
        }
      }
      // Inject pending payouts count badge on "Payouts" item
      if (pendingPayoutsCount > 0) {
        for (const group of groups) {
          for (const item of group.items) {
            if (item.label === 'Payouts') {
              item.badge = String(pendingPayoutsCount);
              item.badgeVariant = 'highlight';
            }
          }
        }
      }
      // Inject unread WhatsApp badge on WhatsApp menu items
      if (unreadWhatsAppCount > 0) {
        const waBadge = unreadWhatsAppCount > 99 ? '99+' : String(unreadWhatsAppCount);
        for (const group of groups) {
          for (const item of group.items) {
            if (item.label === 'WhatsApp' || item.label === 'WhatsApp Inbox') {
              item.badge = waBadge;
              item.badgeVariant = 'highlight';
            }
          }
        }
      }
      return groups;
    },
    [userLoggedIn, studentSelfScheduling, hasPlanFeature, isFeatureHidden, unreadChatCount, pendingPayoutsCount, unreadWhatsAppCount]
  );

  const handleLogout = async () => {
    const redirectPath = userLoggedIn?.usertype === 'tenantadmin' ? '/ta/login' : '/login';
    // Clean up push subscriptions + clear auth data
    await authLogout();
    sessionStorage.clear();
    navigate(redirectPath);
  };

  const userInitials = userLoggedIn?.name
    ? userLoggedIn.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'SA';

  // Branding: tenantadmin sees platform branding; institute users see merged branding
  const isTenantAdmin = userLoggedIn?.usertype === 'tenantadmin';
  const brandName = isTenantAdmin ? (branding.brandName || 'LMS Campus') : branding.brandName;
  const brandSub = isTenantAdmin ? (branding.brandSubtitle || 'Platform Admin') : branding.brandSubtitle;
  const brandLogo = branding.faviconUrl || branding.logoUrl || simpleLogo;
  const logoBgColor = branding.logoBgColor || '#3b82f6';

  // Shared sidebar content (grouped menu + user footer)
  const sidebarContent = (navItemClick) => (
    <>
      <div className="sidebar-navigation-wrapper">
        {menuGroups.map((group, idx) => (
          <MenuGroup
            key={group.label || idx}
            group={group}
            onNavItemClick={navItemClick}
            isLast={idx === menuGroups.length - 1}
          />
        ))}
      </div>
      {userLoggedIn?.usertype !== 'student' && userLoggedIn?.usertype !== 'instructor' && (
        <SidebarTrialBanner trial={trial} onClick={() => navigate('/billing')} />
      )}
      <div className="sidebar-footer">
        <div className="sidebar-user-section">
          <div className="sidebar-user-avatar">{userInitials}</div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{userLoggedIn?.name || 'User'}</span>
            <span className="sidebar-user-role">{userLoggedIn?.usertype || 'User'}</span>
          </div>
          <button className="sidebar-theme-btn" onClick={toggleTheme} title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
        <button className="sidebar-logout-btn" onClick={handleLogout}>
          <LogOut size={16} />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  // Offcanvas (mobile) sidebar
  if (isOffcanvas) {
    return (
      <Offcanvas
        show={show}
        onHide={onHide}
        placement="start"
        className="dashboard-sidebar d-md-none"
      >
        <Offcanvas.Header closeButton className="sidebar-header">
          <Offcanvas.Title>
            <div className="sidebar-logo-wrapper">
              <img src={brandLogo} alt="Logo" className="sidebar-favicon" style={{ background: logoBgColor }} />
              <div className="sidebar-title">
                <div className="sidebar-title-main">{brandName}</div>
                <div className="sidebar-title-sub">{brandSub}</div>
              </div>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="sidebar-body-mobile">
          {sidebarContent(onHide)}
        </Offcanvas.Body>
      </Offcanvas>
    );
  }

  // Static (desktop) sidebar
  return (
    <div className="dashboard-sidebar d-none d-md-flex flex-column">
      <div className="sidebar-header">
        <div className="sidebar-logo-wrapper">
          <img src={brandLogo} alt="Logo" className="sidebar-favicon" style={{ background: logoBgColor }} />
          <div className="sidebar-title">
            <div className="sidebar-title-main">{brandName}</div>
            <div className="sidebar-title-sub">{brandSub}</div>
          </div>
        </div>
      </div>
      {sidebarContent(undefined)}
    </div>
  );
};

export default Sidebar;
